import {
  Button,
  Chip,
  CircularProgress,
  Drawer,
  Stack,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import { FC } from 'react'
import { Header } from 'src/components/header/Header'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import { gray } from 'src/features/muikds/kdsColors'
import { isHTTPError } from 'src/state/middleware/saga/handleHttpError'
import { ExhaustiveError } from 'src/util/error'
import { useQueryGlossaryJobsLatestStatus } from '../api/useQueryGlossaryJobsLatestStatus'
import { GlossaryJobLatestStatus, GlossaryJobStatus } from '../types'

const formatDateTime = (date: Date): string => {
  return format(date, 'yyyy/MM/dd HH:mm:ss')
}

const getStatusChipProps = (status: GlossaryJobStatus) => {
  switch (status) {
    case GlossaryJobStatus.Completed:
      return { label: '成功', color: 'success' as const }
    case GlossaryJobStatus.Failed:
      return { label: '失敗', color: 'error' as const }
    case GlossaryJobStatus.Uploading:
      return { label: '進行中', color: 'default' as const }
    default:
      throw new ExhaustiveError(status)
  }
}

const DetailsContent: FC<{ data: GlossaryJobLatestStatus }> = ({ data }) => {
  const chipProps = getStatusChipProps(data.status)

  return (
    <Stack sx={{ flex: 1 }} padding={3}>
      <Stack gap={3} padding={1}>
        <Stack gap={1}>
          <Typography variant="body1" fontWeight="bold">
            実行開始日時
          </Typography>
          <Typography variant="body1">
            {formatDateTime(data.createdAt)}
          </Typography>
        </Stack>

        <Stack gap={1}>
          <Typography variant="body1" fontWeight="bold">
            CSVファイル名
          </Typography>
          <Typography variant="body1">{data.fileName}</Typography>
        </Stack>

        <Stack gap={1}>
          <Typography variant="body1" fontWeight="bold">
            実行ユーザー表示名（ユーザー管理番号）
          </Typography>
          <Typography variant="body1">
            {`${data.uploaderUserDisplayName}（${data.uploaderRumpCustomerMemberId}）`}
          </Typography>
        </Stack>

        <Stack gap={1}>
          <Typography variant="body1" fontWeight="bold">
            ステータス
          </Typography>
          <Stack direction="row">
            <Chip
              label={chipProps.label}
              color={chipProps.color}
              size="small"
              variant="filled"
            />
          </Stack>
        </Stack>

        {data.status === GlossaryJobStatus.Failed && data.errorDetail && (
          <Stack gap={1}>
            <Typography variant="body1" fontWeight="bold">
              エラー内容
            </Typography>
            <Typography variant="body1">{data.errorDetail}</Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

const LoadingContent: FC = () => (
  <Stack
    sx={{ flex: 1 }}
    alignItems="center"
    justifyContent="center"
    padding={3}
  >
    <CircularProgress />
  </Stack>
)

const NotFoundContent: FC = () => (
  <Stack
    sx={{ flex: 1 }}
    alignItems="center"
    justifyContent="center"
    textAlign="center"
    padding={3}
  >
    <Stack gap={2}>
      <Typography variant="subtitle1">
        アップロード済みのCSVはまだありません
      </Typography>
      <Typography variant="body2">
        CSVがアップロードされるとここに処理状況が表示されます
      </Typography>
    </Stack>
  </Stack>
)

const ErrorContent: FC = () => (
  <Stack
    sx={{ flex: 1 }}
    alignItems="center"
    justifyContent="center"
    textAlign="center"
    padding={3}
  >
    <Stack gap={2}>
      <Typography variant="subtitle1">データの取得に失敗しました</Typography>
      <Typography variant="body2">時間をおいて再度お試しください</Typography>
    </Stack>
  </Stack>
)

type Props = {
  open: boolean
  onClose: () => void
}

export const GlossaryJobStatusDrawer: FC<Props> = ({ open, onClose }) => {
  const { data, isLoading, error } = useQueryGlossaryJobsLatestStatus(open)

  const isNotFound = isHTTPError(error) && error.status === 404

  const renderContent = () => {
    if (isLoading) return <LoadingContent />
    if (isNotFound) return <NotFoundContent />
    if (data) return <DetailsContent data={data} />
    if (error) return <ErrorContent />
    return null
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: '600px' } }}
    >
      <Header>
        <HeaderTitle title="データ処理状況の確認" />
      </Header>

      <Stack sx={{ height: '100%' }}>
        {renderContent()}

        <Stack
          direction="row"
          justifyContent="flex-end"
          padding={3}
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: gray[2],
          }}
        >
          <Button variant="contained" color="secondary" onClick={onClose}>
            閉じる
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  )
}
